var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useMemo } from "react";
import { useGetMany, useGetOne, useListContext } from "react-admin";
import { ELogTimeZonePolicy, } from "@sequentech/ui-core";
import { DEFAULT_TIME_ZONE, logTimeZone, primaryTimeZone } from "@/lib/timezones/zonedFormat";
import { useTenantStore } from "@/providers/TenantContextProvider";
import { useEventPresentation } from "@/hooks/useZonedFormat";
import { useMyTimeZone } from "@/providers/EventTimeZoneProvider";
import { logMessage } from "./logMessage";
import { ELogScope } from "./logScope";
/** The election event's log zones (sequent-core `log_time_zone`). */
export var useLogZones = function (event) {
    var presentation = useEventPresentation(event);
    var myZone = useMyTimeZone();
    return useMemo(function () {
        var _a, _b, _c, _d;
        var configured = (_b = (_a = presentation === null || presentation === void 0 ? void 0 : presentation.timezones) === null || _a === void 0 ? void 0 : _a.configured) !== null && _b !== void 0 ? _b : [];
        var primary = primaryTimeZone(presentation);
        return {
            scope: ELogScope.EVENT,
            presentation: presentation,
            primary: primary,
            byElection: ((_d = (_c = presentation === null || presentation === void 0 ? void 0 : presentation.timezones) === null || _c === void 0 ? void 0 : _c.logs) !== null && _d !== void 0 ? _d : ELogTimeZonePolicy.ELECTION) ===
                ELogTimeZonePolicy.ELECTION && configured.length > 1,
            choices: Array.from(new Set(__spreadArray(__spreadArray([primary], configured, true), [myZone], false))),
        };
    }, [presentation, myZone]);
};
/**
 * The tenant log's zones: the tenant setting `logs_timezone` (UTC when not
 * set), then mine. The tenant is read only when `enabled`.
 */
export var useTenantLogZones = function (enabled) {
    var _a;
    if (enabled === void 0) { enabled = true; }
    var tenantId = useTenantStore()[0];
    var myZone = useMyTimeZone();
    var tenant = useGetOne("sequent_backend_tenant", { id: tenantId !== null && tenantId !== void 0 ? tenantId : "" }, { enabled: enabled && !!tenantId }).data;
    var configured = (_a = tenant === null || tenant === void 0 ? void 0 : tenant.settings) === null || _a === void 0 ? void 0 : _a.logs_timezone;
    return useMemo(function () {
        var primary = configured || DEFAULT_TIME_ZONE;
        return {
            scope: ELogScope.TENANT,
            primary: primary,
            byElection: false,
            choices: Array.from(new Set([primary, myZone])),
        };
    }, [configured, myZone]);
};
/**
 * The zone of a row of the list it is rendered in (inside a react-admin
 * List): the elections of the page's rows are read once, by id, and only when
 * rows can differ.
 */
export var useLogRowZone = function (zones) {
    var rows = useListContext().data;
    var ids = useMemo(function () {
        return zones.byElection
            ? Array.from(new Set((rows !== null && rows !== void 0 ? rows : [])
                .map(function (row) { var _a; return (_a = logMessage(row)) === null || _a === void 0 ? void 0 : _a.election_id; })
                .filter(function (id) { return !!id; }))).sort()
            : [];
    }, [rows, zones.byElection]);
    var elections = useGetMany("sequent_backend_election", { ids: ids }, { enabled: ids.length > 0 }).data;
    return useMemo(function () {
        var presentations = new Map((elections !== null && elections !== void 0 ? elections : []).map(function (election) {
            var _a;
            return [
                String(election.id),
                ((_a = election.presentation) !== null && _a !== void 0 ? _a : undefined),
            ];
        }));
        // Every entry of the tenant log is in the tenant's log zone.
        if (zones.scope === ELogScope.TENANT)
            return function () { return zones.primary; };
        return function (electionId) {
            return logTimeZone(zones.presentation, electionId ? presentations.get(electionId) : undefined);
        };
    }, [elections, zones.presentation, zones.scope, zones.primary]);
};
